import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BedDouble, Bath, Ruler, MapPin, Bookmark, Send, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR, fmtErr, imgSrc } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";

export default function PropertyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setP(null);
    api.get(`/properties/${id}`).then(({ data }) => {
      setP({ ...data, images: (data.images || []).map(imgSrc) });
      setActiveImg(0);
    }).catch(() => setP(false));
  }, [id]);

  useEffect(() => {
    if (user?.role === "buyer") {
      setForm((f) => ({ ...f, name: f.name || user.name, email: f.email || user.email }));
      api.get("/saved").then(({ data }) => {
        setSaved(data.properties.some((x) => x.id === id));
      }).catch(() => {});
    }
  }, [user, id]);

  if (p === null) return <div className="flex min-h-screen items-center justify-center"><p className="font-mono text-xs uppercase tracking-[0.3em] text-gold animate-pulse-soft">Unveiling…</p></div>;
  if (p === false) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6" data-testid="property-not-found">
      <p className="font-serif text-4xl text-bone">This residence is off-market.</p>
      <Link to="/listings" className="gold-link">← Back to the collection</Link>
    </div>
  );

  const submitInquiry = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await api.post("/leads", { property_id: p.id, ...form });
      setSent(true);
      toast.success("Enquiry sent — our desk will reach out shortly.");
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setSending(false);
    }
  };

  const toggleSave = async () => {
    if (!user) return toast.error("Sign in as a buyer to save residences.");
    try {
      if (saved) {
        await api.delete(`/saved/${p.id}`);
        setSaved(false);
        toast.success("Removed from your collection");
      } else {
        await api.post(`/saved/${p.id}`);
        setSaved(true);
        toast.success("Saved to your collection");
      }
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="property-detail-page">
      <div className="mx-auto max-w-[1600px] px-6 pb-32 lg:px-12">
        <Link to="/listings" data-testid="back-to-listings" className="mb-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-smoke transition-colors hover:text-gold">
          <ArrowLeft size={14} /> All Residences
        </Link>

        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <Reveal>
              <div className="relative aspect-[16/10] overflow-hidden border border-white/10" data-testid="property-gallery">
                <img src={p.images?.[activeImg]} alt={p.title} className="h-full w-full object-cover" />
                <span className="absolute left-5 top-5 bg-black/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-gold backdrop-blur-md">
                  {p.listing_type === "rent" ? "For Lease" : "For Sale"}
                </span>
              </div>
              {p.images?.length > 1 && (
                <div className="mt-4 flex gap-4">
                  {p.images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      data-testid={`gallery-thumb-${i}`}
                      className={`aspect-[16/10] w-28 overflow-hidden border transition-colors ${i === activeImg ? "border-gold" : "border-white/10 opacity-60 hover:opacity-100"}`}
                    >
                      <img src={img} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-12">
                <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-gold"><MapPin size={13} /> {p.locality}, {p.city}</p>
                <h1 className="mt-4 font-serif text-5xl font-light leading-tight text-bone sm:text-6xl" data-testid="property-title">{p.title}</h1>
                {p.project_name && <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-smoke">A {p.project_name} residence</p>}
                <p className="mt-8 max-w-2xl text-base font-light leading-relaxed text-smoke" data-testid="property-description">{p.description}</p>

                {p.amenities?.length > 0 && (
                  <div className="mt-10" data-testid="amenities-list">
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Amenities</p>
                    <div className="mt-4 flex flex-wrap gap-3">
                      {p.amenities.map((a) => (
                        <span key={a} className="border border-white/15 px-4 py-2 text-xs font-light text-bone/80">{a}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-4">
            <div className="sticky top-28 space-y-6">
              <Reveal delay={0.15}>
                <div className="border border-white/10 bg-obsidian p-8">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">Asking</p>
                  <p className="mt-2 font-serif text-4xl font-light text-gold" data-testid="property-price">{formatINR(p.price, p.price_unit)}</p>
                  <div className="mt-6 grid grid-cols-3 gap-4 border-t border-white/10 pt-6 text-center">
                    <div><BedDouble size={16} className="mx-auto text-gold" /><p className="mt-2 text-sm text-bone">{p.beds}</p><p className="font-mono text-[9px] uppercase tracking-widest text-smoke">Beds</p></div>
                    <div><Bath size={16} className="mx-auto text-gold" /><p className="mt-2 text-sm text-bone">{p.baths}</p><p className="font-mono text-[9px] uppercase tracking-widest text-smoke">Baths</p></div>
                    <div><Ruler size={16} className="mx-auto text-gold" /><p className="mt-2 text-sm text-bone">{p.sqft?.toLocaleString("en-IN")}</p><p className="font-mono text-[9px] uppercase tracking-widest text-smoke">Ft²</p></div>
                  </div>
                  <button
                    onClick={toggleSave}
                    data-testid="save-property-button"
                    className={`mt-6 flex w-full items-center justify-center gap-2 border px-4 py-3 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors ${saved ? "border-gold bg-gold text-ink" : "border-white/20 text-bone hover:border-gold hover:text-gold"}`}
                  >
                    <Bookmark size={14} fill={saved ? "currentColor" : "none"} /> {saved ? "Saved" : "Save Residence"}
                  </button>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="border border-white/10 bg-obsidian p-8">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Listed by</p>
                  <p className="mt-3 font-serif text-2xl text-bone">{p.owner?.name || p.owner_name}</p>
                  <p className="mt-1 text-xs font-light text-smoke">{p.owner?.company || p.owner_company} · {p.owner_role === "builder" ? "Developer" : "Broker"}</p>

                  {sent ? (
                    <div className="mt-8 border border-gold/40 bg-gold/10 p-5" data-testid="inquiry-success">
                      <p className="font-serif text-xl text-gold">Enquiry received.</p>
                      <p className="mt-2 text-xs font-light leading-relaxed text-bone/70">Our desk routes every enquiry by rule — expect a call within hours, not days.</p>
                    </div>
                  ) : (
                    <form onSubmit={submitInquiry} className="mt-8 space-y-4" data-testid="inquiry-form">
                      {[
                        { k: "name", label: "Name", type: "text", testid: "inquiry-name" },
                        { k: "email", label: "Email", type: "email", testid: "inquiry-email" },
                        { k: "phone", label: "Phone", type: "tel", testid: "inquiry-phone" },
                      ].map((f) => (
                        <input
                          key={f.k}
                          required={f.k !== "phone"}
                          type={f.type}
                          placeholder={f.label}
                          value={form[f.k]}
                          onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                          data-testid={f.testid}
                          className="w-full border border-white/15 bg-transparent px-4 py-3 text-sm font-light text-bone outline-none transition-colors placeholder:text-smoke/60 focus:border-gold"
                        />
                      ))}
                      <textarea
                        placeholder="Your note — preferred visit time, questions…"
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        data-testid="inquiry-message"
                        rows={3}
                        className="w-full resize-none border border-white/15 bg-transparent px-4 py-3 text-sm font-light text-bone outline-none transition-colors placeholder:text-smoke/60 focus:border-gold"
                      />
                      {error && <p className="text-xs text-red-400" data-testid="inquiry-error">{error}</p>}
                      <button
                        type="submit"
                        disabled={sending}
                        data-testid="inquiry-submit-button"
                        className="flex w-full items-center justify-center gap-2 bg-gold px-4 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-ink transition-colors hover:bg-bone disabled:opacity-50"
                      >
                        <Send size={13} /> {sending ? "Sending…" : "Request a Walkthrough"}
                      </button>
                    </form>
                  )}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
